/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from "react";

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import Alert from '@material-ui/lab/Alert';
import classNames from "classnames";

import { CirclePreloader } from "../../../elements/CirclePreloader";
import { useStyles } from "./styles";

interface ConfirmationDialogProps {
    open: boolean,
    dialogTitle: string,
    dialogContentText: string,
    okayButtonLabel: string,
    cancelButtonLabel: string,
    okButtonTestId?: string,
    cancelButtonTestId?: string,
    loadingActionHelperText?: string,
    isActionProcessing?: boolean,
    error?: string,
    onClose: () => void,
    onOkay: () => void,
    onCancel: () => void,
}

export const ConfirmationDialog = (props: ConfirmationDialogProps) => {
    const {
        open, dialogTitle, dialogContentText, okayButtonLabel, cancelButtonLabel, loadingActionHelperText,
        isActionProcessing, error, onClose, onOkay, onCancel, okButtonTestId, cancelButtonTestId
    } = props;
    const classes = useStyles();

    const Loader = () => {
        return (
            <div className={classNames(classes.loader, classes.actionLoader)}>
                <CirclePreloader position="relative" />
                <Typography variant="body1" className={classes.loaderText}> {loadingActionHelperText} </Typography>
            </div>
        );
    };

    const DialogContents = () => {
        return (
            <div>
                <Typography gutterBottom={true} variant="subtitle2" className={classes.secondaryTitle}>
                    {dialogContentText}
                </Typography>
                {isActionProcessing && <Loader />}
            </div>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="confirmation-dialog"
            fullWidth={true}
            maxWidth="xs"
            classes={{paper: classes.dialogRootStyle}}
        >
            <DialogTitle>
                {
                    onClose ? (
                        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    ) : null
                }
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Typography
                    data-testid={dialogTitle}
                    variant="h1"
                    component="div"
                    className={classes.primaryTitle}
                >
                    {dialogTitle}
                </Typography>
                <div className={classes.dialogContent}>
                    {error && <Alert variant="standard" severity="error"> {error} </Alert>}
                    <DialogContents />
                </div>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    disabled={isActionProcessing}
                    variant="contained"
                    color="default"
                    classes={{ root: classes.dialogButton }}
                    onClick={onCancel}
                    data-testid={cancelButtonTestId}
                >
                    {cancelButtonLabel}
                </Button>
                <Button
                    disabled={isActionProcessing}
                    variant="contained"
                    color="primary"
                    classes={{ root: classes.dialogButton }}
                    onClick={onOkay}
                    data-testid={okButtonTestId}
                >
                    {okayButtonLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
